import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getListOrganizationApiKeysQueryKey, useCreateOrganizationApiKey } from '@sara/api-client';
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@sara/ui/components/dialog';
import type { DialogBaseProps } from '@sara/ui/components/dialog-provider';
import { toast } from '@sara/ui/components/sonner';
import { ApiKeySecretReveal } from '../../components/ApiKeySecretReveal';
import { CreateApiKeyForm, type CreateApiKeyFormValues } from '../../components/CreateApiKeyForm';

interface CreateApiKeyDialogProps extends DialogBaseProps {
  orgId: string;
}

function CreateApiKeyDialog({ orgId, onClose }: CreateApiKeyDialogProps) {
  const queryClient = useQueryClient();
  const [secret, setSecret] = useState<string | null>(null);

  const { mutate, isPending, error } = useCreateOrganizationApiKey({
    mutation: {
      onSuccess: (data) => {
        void queryClient.invalidateQueries({
          queryKey: getListOrganizationApiKeysQueryKey({ orgId }),
        });
        setSecret(data.key);
        toast.success(`Created ${data.label}`, {
          description: 'Copy the key now — it won’t be shown again.',
        });
      },
    },
  });

  function handleSubmit(values: CreateApiKeyFormValues) {
    mutate({ data: { label: values.label }, params: { orgId } });
  }

  if (secret) {
    return (
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium tracking-tight">
            API key created
          </DialogTitle>
          <DialogDescription>
            Copy your new API key below. It won&rsquo;t be shown again.
          </DialogDescription>
        </DialogHeader>
        <ApiKeySecretReveal secret={secret} onDone={onClose} />
      </DialogContent>
    );
  }

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle className="font-display text-2xl font-medium tracking-tight">
          Create API key
        </DialogTitle>
        <DialogDescription>
          Give this key a descriptive label so you can identify it later.
        </DialogDescription>
      </DialogHeader>
      <CreateApiKeyForm
        onSubmit={handleSubmit}
        onCancel={onClose}
        isPending={isPending}
        error={error instanceof Error ? error.message : error ? 'Failed to create API key' : null}
      />
    </DialogContent>
  );
}

export { CreateApiKeyDialog };
